import React, { useEffect } from 'react';
import { X, MapPin, Clock, Globe, Info, Share2, MessageCircle, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getReviewStrings } from '@/utils/reviewStrings';

/**
 * Right-side drawer menu opened from the sticky header hamburger icon.
 * Contains the 7 items requested by the user, in the exact order:
 *   1. Location (city selector)
 *   2. Clock type (digital / analog)
 *   3. Language
 *   4. About Falak (merged with the former Falak Services page)
 *   5. Rate the App
 *   6. Share
 *   7. Feedback
 *
 * The individual pill-shaped components (CitySelector, ClockTypeSelector,
 * ShareButton, FeedbackButton) are reused as-is inside the drawer so their
 * existing dropdowns/dialogs keep working.
 */

const LABELS = {
  en: { menu: 'Menu', location: 'Location', clock: 'Clock Style', language: 'Language', about: 'About Falak', share: 'Share', feedback: 'Share Feedback' },
  ar: { menu: 'القائمة', location: 'الموقع الجغرافي', clock: 'نوع الساعة', language: 'اللغة', about: 'عن فلك', share: 'مشاركة', feedback: 'شاركنا رأيك' },
  ur: { menu: 'مینو', location: 'مقام', clock: 'گھڑی کی قسم', language: 'زبان', about: 'فلک کے بارے میں', share: 'شیئر کریں', feedback: 'رائے شیئر کریں' },
  zh: { menu: '菜单', location: '位置', clock: '时钟样式', language: '语言', about: '关于 Falak', share: '分享', feedback: '分享反馈' },
  fa: { menu: 'منو', location: 'موقعیت', clock: 'نوع ساعت', language: 'زبان', about: 'درباره فلک', share: 'اشتراک‌گذاری', feedback: 'ارسال بازخورد' },
  tr: { menu: 'Menü', location: 'Konum', clock: 'Saat Türü', language: 'Dil', about: 'Falak Hakkında', share: 'Paylaş', feedback: 'Geri Bildirim' },
  fr: { menu: 'Menu', location: 'Emplacement', clock: 'Type d\'horloge', language: 'Langue', about: 'À propos de Falak', share: 'Partager', feedback: 'Vos retours' },
  bn: { menu: 'মেনু', location: 'অবস্থান', clock: 'ঘড়ির ধরন', language: 'ভাষা', about: 'ফলক সম্পর্কে', share: 'শেয়ার', feedback: 'মতামত পাঠান' },
  id: { menu: 'Menu', location: 'Lokasi', clock: 'Tipe Jam', language: 'Bahasa', about: 'Tentang Falak', share: 'Bagikan', feedback: 'Kirim Masukan' },
  es: { menu: 'Menú', location: 'Ubicación', clock: 'Tipo de reloj', language: 'Idioma', about: 'Acerca de Falak', share: 'Compartir', feedback: 'Enviar comentarios' },
  pt: { menu: 'Menu', location: 'Localização', clock: 'Tipo de relógio', language: 'Idioma', about: 'Sobre o Falak', share: 'Compartilhar', feedback: 'Enviar feedback' },
  de: { menu: 'Menü', location: 'Standort', clock: 'Uhr-Stil', language: 'Sprache', about: 'Über Falak', share: 'Teilen', feedback: 'Feedback senden' },
  it: { menu: 'Menu', location: 'Posizione', clock: 'Stile orologio', language: 'Lingua', about: 'Informazioni su Falak', share: 'Condividi', feedback: 'Invia feedback' },
};

const HamburgerMenu = ({
  open,
  onClose,
  onOpenLanguage,
  onOpenReview,
  citySelectorSlot,
  clockTypeSelectorSlot,
  shareButtonSlot,
  feedbackButtonSlot,
  currentLangLabel,
}) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const isRTL = ['ar', 'ur', 'fa'].includes(lang);
  const L = LABELS[lang] || LABELS.en;
  const RL = getReviewStrings(lang);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 12px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.03)',
    color: '#E5E7EB',
    fontSize: 15,
    textDecoration: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: isRTL ? 'right' : 'left',
    justifyContent: 'flex-start',
  };

  const iconColor = '#F59E0B';

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[9998]"
      style={{ background: 'rgba(3, 7, 18, 0.55)', backdropFilter: 'blur(4px)' }}
      data-testid="hamburger-backdrop"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 h-full w-[86%] max-w-[340px] overflow-y-auto p-5"
        style={{
          [isRTL ? 'right' : 'left']: 0,
          background: 'linear-gradient(180deg, #14213D 0%, #0B132B 100%)',
          borderLeft: !isRTL ? 'none' : '1px solid rgba(245, 158, 11, 0.25)',
          borderRight: isRTL ? 'none' : '1px solid rgba(245, 158, 11, 0.25)',
          boxShadow: '0 0 40px rgba(0,0,0,0.6)',
        }}
        dir={isRTL ? 'rtl' : 'ltr'}
        data-testid="hamburger-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5 pb-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <h3 className="font-semibold text-lg" style={{ color: '#F1F5F9' }}>{L.menu}</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/10 transition-all"
            data-testid="hamburger-close"
            aria-label="Close"
          >
            <X size={20} style={{ color: '#94A3B8' }} />
          </button>
        </div>

        {/* Menu items */}
        <div className="flex flex-col gap-2">
          {/* 1. Location */}
          <div style={rowStyle} data-testid="menu-item-location">
            <MapPin size={18} style={{ color: iconColor, flexShrink: 0 }} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: '#94A3B8' }}>{L.location}</div>
              {citySelectorSlot}
            </div>
          </div>

          {/* 2. Clock type */}
          <div style={rowStyle} data-testid="menu-item-clock">
            <Clock size={18} style={{ color: iconColor, flexShrink: 0 }} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: '#94A3B8' }}>{L.clock}</div>
              {clockTypeSelectorSlot}
            </div>
          </div>

          {/* 3. Language */}
          <button
            onClick={() => { onOpenLanguage(); onClose(); }}
            style={rowStyle}
            data-testid="menu-item-language"
          >
            <Globe size={18} style={{ color: iconColor, flexShrink: 0 }} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: '#94A3B8' }}>{L.language}</div>
              <div className="text-sm font-medium" style={{ color: '#F1F5F9' }}>{currentLangLabel}</div>
            </div>
          </button>

          {/* 4. About */}
          <a href="/about" style={rowStyle} data-testid="menu-item-about">
            <Info size={18} style={{ color: iconColor, flexShrink: 0 }} />
            <span className="font-medium">{L.about}</span>
          </a>

          {/* 5. Rate the App (opens Smart Review Modal) */}
          <button
            onClick={() => { if (onOpenReview) onOpenReview(); }}
            style={{
              ...rowStyle,
              background: 'linear-gradient(90deg, rgba(245,158,11,0.10), rgba(245,158,11,0.02))',
              border: '1px solid rgba(245,158,11,0.30)',
            }}
            data-testid="menu-item-rate"
          >
            <Star size={18} fill={iconColor} color={iconColor} style={{ flexShrink: 0 }} />
            <div className="flex-1 min-w-0" style={{ textAlign: isRTL ? 'right' : 'left' }}>
              <div className="text-sm font-semibold" style={{ color: '#F1F5F9' }}>{RL.menuLabel}</div>
              <div className="text-[11px]" style={{ color: '#94A3B8', marginTop: 2 }}>{RL.hint}</div>
            </div>
          </button>

          {/* 6. Share */}
          <div style={rowStyle} data-testid="menu-item-share">
            <Share2 size={18} style={{ color: iconColor, flexShrink: 0 }} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: '#94A3B8' }}>{L.share}</div>
              {shareButtonSlot}
            </div>
          </div>

          {/* 7. Feedback */}
          <div style={rowStyle} data-testid="menu-item-feedback">
            <MessageCircle size={18} style={{ color: iconColor, flexShrink: 0 }} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: '#94A3B8' }}>{L.feedback}</div>
              {feedbackButtonSlot}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default HamburgerMenu;
